/**
 * Generic filter store factory for Zustand
 * Creates standardized filter stores for any module
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { BaseFilters } from '@/types/resource.types';
import type { FilterStoreState, FilterStoreConfig } from '@/types/store.types';
import { registerPersistedFilterStore } from '@/store/filter-scope';
import { createSelectors } from './createSelectors';

const areFilterValuesEqual = (previous: unknown, next: unknown) => {
  if (Object.is(previous, next)) return true
  if (Array.isArray(previous) && Array.isArray(next)) {
    return (
      previous.length === next.length &&
      previous.every((value, index) => Object.is(value, next[index]))
    )
  }
  return false
}

const areFiltersEqual = (
  previous: Record<string, unknown>,
  next: Record<string, unknown>,
) => {
  const keys = new Set([...Object.keys(previous), ...Object.keys(next)])
  for (const key of keys) {
    if (!areFilterValuesEqual(previous[key], next[key])) {
      return false
    }
  }
  return true
}

/**
 * Creates a filter store for a module
 * 
 * @example
 * ```ts
 * export const useProjectFiltersStore = createFilterStore<ProjectFilters>({
 *   storageKey: 'project-filters-storage',
 *   defaultFilters: {
 *     page: 1,
 *     limit: 50,
 *     sortBy: 'createdAt',
 *     sortOrder: 'desc',
 *     name: '',
 *     status: '',
 *     includeCustomFields: true,
 *   },
 *   autoResetPage: true,
 *   resetPageFields: ['name', 'status', 'sortBy', 'sortOrder'],
 * });
 * ```
 */
export function createFilterStore<TFilters extends BaseFilters>(
  config: FilterStoreConfig<TFilters>
) {
  const {
    storageKey,
    defaultFilters,
    autoResetPage = true,
    resetPageFields = [],
    sanitizePersistedFilters,
  } = config;

  const store = create<FilterStoreState<TFilters>>()(
    persist(
      (set) => ({
        filters: { ...defaultFilters },

        setFilters: (newFilters: Partial<TFilters>) => {
          set((state) => {
            const shouldResetPage = (() => {
              if (!autoResetPage) return false;
              if (resetPageFields.length === 0) {
                return Object.keys(newFilters).some((key) => key !== 'page' && key !== 'limit');
              }
              return resetPageFields.some((field) => field in newFilters);
            })();

            const updatedFilters = { ...state.filters, ...newFilters };

            if (shouldResetPage && !('page' in newFilters)) {
              updatedFilters.page = 1 as TFilters['page'];
            }

            if (
              areFiltersEqual(
                state.filters as Record<string, unknown>,
                updatedFilters as Record<string, unknown>,
              )
            ) {
              return state;
            }

            return { filters: updatedFilters };
          });
        },

        clearFilters: () =>
          set(() => ({
            filters: { ...defaultFilters },
          })),

        resetPagination: () =>
          set((state) => ({
            filters: { ...state.filters, page: 1 as TFilters['page'] },
          })),
      }),
      {
        name: storageKey,
        // Version 1 intentionally discards the legacy browser-wide filter
        // payload once. Future scope changes are handled by filter-scope.ts.
        version: 1,
        migrate: () => ({ filters: { ...defaultFilters } }),
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          filters: state.filters,
        }),
        // Merge stored filters with defaults, ensuring limit always uses default value
        // This prevents stale limit values from causing duplicate queries
        merge: (persistedState, currentState) => {
          const persisted = persistedState as Partial<FilterStoreState<TFilters>>;
          const persistedFilters =
            (persisted.filters as Partial<TFilters> & Record<string, unknown>) || {};
          const sanitizedFilters = sanitizePersistedFilters
            ? sanitizePersistedFilters(persistedFilters)
            : persistedFilters;
          return {
            ...currentState,
            filters: {
              ...currentState.filters,
              ...sanitizedFilters,
              // Always use default limit to avoid stale pagination values
              limit: defaultFilters.limit,
              // Always restart at the default page: a persisted page can be
              // out of range after the dataset shrinks, which renders an empty
              // page indistinguishable from a truly empty list (and BaseTable
              // blocks paging past the new total, so the user can't recover)
              page: defaultFilters.page,
            },
          };
        },
      }
    )
  );

  registerPersistedFilterStore(storageKey, () => {
    store.getState().clearFilters()
  })

  return createSelectors(store);
}

/**
 * Type helper to extract filters type from a filter store
 */
export type ExtractFilters<T> = T extends { filters: infer F } ? F : never;
